import { Controller } from "react-hook-form";

import { FieldError, Input, Label, TextField } from "@/components/field";
import {
  Select,
  SelectButton,
  SelectListBox,
  SelectListItem,
  SelectPopover,
} from "@/components/select";
import { joinTags, splitTags, TagInput } from "@/components/tag-input";
import { CollaboratorsSelect } from "@/features/request/create/shared/components/CollaboratorsSelect.tsx";
import { PreferredDesignersSelect } from "@/features/request/create/shared/components/PreferredDesignersSelect.tsx";
import { AiAssetsToggle } from "@/features/request/shared/components/AiAssetsToggle";
import {
  FILE_TYPE_SUGGESTIONS,
  SIZE_SUGGESTIONS,
} from "@/features/shared/constants/technicalDetailsConstants";
import { useUser } from "@/hooks/useUser";

import { useCreateVideoRequestFormContext } from "../useCreateVideoRequestForm";
import { ComplexitySelect } from "./ComplexitySelect";

export function TechnicalDetailsSection() {
  const { data: user } = useUser();
  const methods = useCreateVideoRequestFormContext();

  const showComplexityField = [
    "customer-success",
    "super-admin",
    "team-leader",
  ].includes(user?.user.roleName || "");

  return (
    <div className="flex flex-col gap-lg">
      <span className="col-span-12 text-lg font-semibold text-text-primary">
        Technical Details
      </span>
      <AiAssetsToggle control={methods.control} name="restrictAiAssets" />
      <div className="flex flex-col gap-y-lg lg:flex-row lg:gap-3xl">
        <Controller
          control={methods.control}
          name="creativeDirection"
          render={({ field: { name, onBlur, onChange, value } }) => (
            <Select
              className="flex-1"
              name={name}
              onBlur={onBlur}
              onSelectionChange={onChange}
              placeholder="Select creative direction"
              selectedKey={value}
            >
              <Label>Creative direction</Label>
              <SelectButton />
              <SelectPopover>
                <SelectListBox>
                  <SelectListItem id="Designer has creative freedom">
                    Designer has creative freedom
                  </SelectListItem>
                  <SelectListItem id="Follow brief exactly">
                    Follow brief exactly
                  </SelectListItem>
                </SelectListBox>
              </SelectPopover>
            </Select>
          )}
        />
        <Controller
          control={methods.control}
          name="sizes"
          render={({
            field: { name, onBlur, onChange, value },
            fieldState: { error, invalid },
          }) => (
            <TagInput
              className="flex-1"
              errorMessage={error?.message}
              isInvalid={invalid}
              label="Sizes needed"
              name={name}
              onBlur={onBlur}
              onChange={(tags) => onChange(joinTags(tags))}
              placeholder="E.g. 1080x1080, 16x9, etc."
              requiredHint
              suggestions={SIZE_SUGGESTIONS}
              value={splitTags(value)}
            />
          )}
        />
        <Controller
          control={methods.control}
          name="lengthOrPlatform"
          render={({
            field: { name, onBlur, onChange, ref, value },
            fieldState: { error, invalid },
          }) => (
            <TextField
              className="flex-1"
              isInvalid={invalid}
              name={name}
              onBlur={onBlur}
              onChange={onChange}
              validationBehavior="aria"
              value={value}
            >
              <Label requiredHint>Length or platform</Label>
              <Input
                placeholder="Indicate length of video or platform"
                ref={ref}
              />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />
        <Controller
          control={methods.control}
          name="style"
          render={({
            field: { name, onBlur, onChange, ref, value },
            fieldState: { error, invalid },
          }) => (
            <TextField
              className="flex-1"
              isInvalid={invalid}
              name={name}
              onBlur={onBlur}
              onChange={onChange}
              validationBehavior="aria"
              value={value}
            >
              <Label>Style of video</Label>
              <Input placeholder="Indicate style of video" ref={ref} />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />
      </div>
      <div className="flex flex-col gap-y-lg lg:flex-row lg:gap-3xl">
        <Controller
          control={methods.control}
          name="musicGenre"
          render={({
            field: { name, onBlur, onChange, ref, value },
            fieldState: { error, invalid },
          }) => (
            <TextField
              className="flex-1"
              isInvalid={invalid}
              name={name}
              onBlur={onBlur}
              onChange={onChange}
              validationBehavior="aria"
              value={value}
            >
              <Label>Music genre</Label>
              <Input placeholder="Indicate type of music" ref={ref} />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />
        <Controller
          control={methods.control}
          name="fileTypes"
          render={({
            field: { name, onBlur, onChange, value },
            fieldState: { error, invalid },
          }) => (
            <TagInput
              className="flex-1"
              errorMessage={error?.message}
              isInvalid={invalid}
              label="File types"
              name={name}
              onBlur={onBlur}
              onChange={(tags) => onChange(joinTags(tags))}
              placeholder="E.g. MP4, MOV, etc."
              requiredHint
              suggestions={FILE_TYPE_SUGGESTIONS.video}
              value={splitTags(value)}
            />
          )}
        />
        <PreferredDesignersSelect methods={methods} />
        {showComplexityField && <ComplexitySelect />}
      </div>
      <div className="grid grid-cols-12 gap-3xl">
        <CollaboratorsSelect methods={methods} />
      </div>
    </div>
  );
}
